'use client';

import { isPaymentVerificationCancelledError, paymentFetch } from '@/lib/payment-risk/client';
import { useCallback, useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ToastProvider';
import { fetchCachedApi, CLIENT_CACHE_KEYS, CLIENT_CACHE_EVENTS } from '@/lib/client-request-cache';

const DAY_MS = 24 * 60 * 60 * 1000;

const getDismissKey = (userId) => `past_due_modal_dismissed_at:${userId}`;

export default function PastDueModal() {
    const t = useTranslations('Settings.billing');
    const tCommon = useTranslations('Common');
    const pathname = usePathname();
    const { showToast } = useToast();

    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [userId, setUserId] = useState(null);

    const checkAndShow = useCallback(async () => {
        // 1. 获取订阅状态
        const result = await fetchCachedApi('/api/user/subscription', {
            cacheKey: CLIENT_CACHE_KEYS.subscription,
            ttlMs: 60000,
            force: true, // 强制刷新以获取最新状态
        });

        if (!result.ok) {
            return;
        }

        const subscription = result.data?.subscription;

        // 2. 检查是否为 past_due 状态
        if (!subscription || subscription.status !== 'past_due') {
            setIsOpen(false);
            return;
        }

        const currentUserId = subscription.userId;
        setUserId(currentUserId);

        if (!currentUserId || typeof window === 'undefined') {
            return;
        }

        // 3. 检查冷却时间 (24h)
        const key = getDismissKey(currentUserId);
        const lastDismissedRaw = window.localStorage.getItem(key);

        if (lastDismissedRaw) {
            const lastDismissedAt = Number(lastDismissedRaw);
            if (Number.isFinite(lastDismissedAt)) {
                const elapsed = Date.now() - lastDismissedAt;
                if (elapsed < DAY_MS) {
                    // 仍在冷却期内
                    return;
                }
            }
        }

        // 4. 显示弹窗
        setIsOpen(true);
    }, []);

    // 监听路由变化重新检查（确保用户并未刚刚修复）
    useEffect(() => {
        checkAndShow();
    }, [checkAndShow, pathname]);

    // 监听订阅更新事件（如用户在另一标签页完成了支付）
    useEffect(() => {
        const handler = () => checkAndShow();
        window.addEventListener(CLIENT_CACHE_EVENTS.subscriptionUpdated, handler);
        return () => {
            window.removeEventListener(CLIENT_CACHE_EVENTS.subscriptionUpdated, handler);
        };
    }, [checkAndShow]);

    const handleUpdatePayment = async () => {
        setLoading(true);
        try {
            const response = await paymentFetch('/api/customer-portal', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });
            const data = await response.json();

            if (!response.ok) {
                throw new Error(data?.error || 'Failed to open billing portal');
            }

            // 跳转到 Stripe Customer Portal
            window.location.href = data.url;
        } catch (error) {
            if (isPaymentVerificationCancelledError(error)) {
                setLoading(false);
                return;
            }
            console.error('Failed to open billing portal:', error);
            showToast({
                title: tCommon('error'),
                description: error.message || tCommon('error'),
                type: 'error',
            });
            setLoading(false);
        }
    };

    const handleDismiss = () => {
        if (userId) {
            const key = getDismissKey(userId);
            window.localStorage.setItem(key, String(Date.now()));
        }
        setIsOpen(false);
    };

    return (
        <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
            <AlertDialogContent className="bg-neutral-900 border-neutral-700 p-8 gap-8 max-w-md">
                <AlertDialogHeader className="space-y-3">
                    <AlertDialogTitle className="text-white text-2xl font-bold text-center">
                        {t('pastDue.title')}
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-neutral-200 text-base leading-relaxed text-center">
                        {t('pastDue.desc')}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter className="sm:space-x-4">
                    {/* 次要按钮：稍后提醒 */}
                    <AlertDialogCancel
                        onClick={handleDismiss}
                        className="bg-transparent border-neutral-700 text-white hover:bg-neutral-800 hover:text-white px-6"
                    >
                        {tCommon('dismiss')}
                    </AlertDialogCancel>

                    {/* 主要按钮：更新支付方式 */}
                    <AlertDialogAction
                        onClick={(e) => {
                            e.preventDefault(); // 阻止自动关闭，等待跳转
                            handleUpdatePayment();
                        }}
                        disabled={loading}
                        className="bg-gradient-to-r from-amber-500 to-orange-600 text-white hover:from-amber-600 hover:to-orange-700 border-none px-6"
                    >
                        {loading ? tCommon('loading') : t('pastDue.cta')}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
